import { createStyles, LoadingOverlay } from '@mantine/core';
import React, { FC, useEffect, useMemo } from 'react';

import { DeviceModelType, useDeviceModel } from '@portals/api/partners';
import {
  LocationTabs,
  useHasSupportSeat,
  usePermissionAccess,
} from '@portals/framework';
import { NewDeviceWidgetsProps } from '@portals/framework/modals';
import { useOpenModal } from '@portals/redux';
import {
  PartnerFeatureFlagsType,
  PartnerPortalCapabilities,
  TabType,
} from '@portals/types';

import { useModelMigrationContext } from '../ModelMigrationProvider';
import { ConfigSchema } from './ConfigSchema';
import { ModelBaseInfo } from './ModelBaseInfo';
import { ModelCommands } from './ModelCommands';
import { ModelFiles } from './ModelFiles';
import { ModelHardwareKeys } from './ModelHardwareKeys';
import { ModelImage } from './ModelImage';
import ModelIntegrations from './ModelIntegrations';
import ModelRules from './ModelRules';
import { useIsLegacyWidgets } from './monitoring-dashboard/monitoring-dashboard.hooks';
import { MonitoringDashboardWrapper } from './monitoring-dashboard/MonitoringDashboardWrapper';

interface TabComponentProps {
  model: DeviceModelType | undefined;
  onDuplicate: (model: DeviceModelType) => void;
  onEdit: (model: DeviceModelType) => void;
  onDelete: (model: DeviceModelType) => void;
  onPublish: (model: DeviceModelType) => void;
}

interface ModelDetailsProps {
  modelId: string;
  onDuplicate: (model: DeviceModelType) => void;
  onEdit: (model: DeviceModelType) => void;
  onDelete: (model: DeviceModelType) => void;
  onPublish: (model: DeviceModelType) => void;
}

const ModelDetails: FC<ModelDetailsProps> = ({
  modelId,
  onDuplicate,
  onEdit,
  onDelete,
  onPublish,
}) => {
  const { classes } = useStyles();
  const model = useDeviceModel(modelId);

  const hasSupportSeat = useHasSupportSeat();
  const { canEdit } = usePermissionAccess();
  const openModal = useOpenModal();

  const legacyWidgets = useIsLegacyWidgets(modelId);
  const modelMigrationContext = useModelMigrationContext();

  const tabs = useMemo<
    TabType<
      PartnerFeatureFlagsType,
      PartnerPortalCapabilities,
      TabComponentProps
    >[]
  >(
    () => [
      {
        id: 'info',
        name: 'info',
        title: 'Info',
        Component: ModelBaseInfo,
      },
      {
        id: 'monitoring_dashboard',
        name: 'monitoring_dashboard',
        title: 'Monitoring Dashboard',
        Component: MonitoringDashboardWrapper,
      },
      {
        id: 'files',
        name: 'files',
        title: 'Files',
        Component: ModelFiles,
      },
      {
        id: 'commands',
        name: 'commands',
        title: 'Supported Commands',
        Component: ModelCommands,
      },
      {
        id: 'rules',
        name: 'rules',
        title: 'Rules',
        Component: ModelRules,
      },
      {
        id: 'schema',
        name: 'schema',
        title: 'Config Schema',
        Component: ConfigSchema,
      },
      {
        id: 'image',
        name: 'image',
        title: 'Image',
        Component: ModelImage,
        visible: ({ canEdit }) => canEdit('models'),
      },
      {
        id: 'integrations',
        name: 'integrations',
        title: 'Integrations',
        Component: ModelIntegrations,
      },
      {
        id: 'hardware_keys',
        name: 'hardware_keys',
        title: 'Hardware Keys',
        Component: ModelHardwareKeys,
        visible: ({ canView }) => hasSupportSeat || canView('models'),
      },
    ],
    [hasSupportSeat]
  );

  useEffect(
    function displayDashboardMigrationNotification() {
      if (!canEdit('models')) return;

      const hasLegacyWidgets =
        !legacyWidgets.isLoading && legacyWidgets.isLegacy;
      const hasDismissedNotification =
        modelMigrationContext.modelsMigrationNotifications.has(modelId);

      if (!hasLegacyWidgets || hasDismissedNotification) {
        return;
      }

      openModal<NewDeviceWidgetsProps['data']>('NewDeviceWidgets', {
        modelId,
        onDismiss: () => modelMigrationContext.onToggleNotification(modelId),
      });
    },
    [canEdit, legacyWidgets, modelId, openModal, modelMigrationContext]
  );

  return (
    <div className={classes.container}>
      <LoadingOverlay visible={!model.isFetched} />

      {!model.isFetched ? null : (
        <LocationTabs<
          PartnerFeatureFlagsType,
          PartnerPortalCapabilities,
          TabComponentProps,
          { id: string }
        >
          tabs={tabs}
          tabContentComponentProps={{
            model: model.data,
            onDuplicate,
            onEdit,
            onDelete,
            onPublish,
          }}
          pathParams={{ id: modelId }}
        />
      )}
    </div>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    '.tab-wrapper': {
      padding: theme.spacing.md,
      border: `1px solid ${theme.colors.blue_gray[0]}`,
      borderTop: 'none',
    },
  },
}));

export default ModelDetails;
